import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  Icon,
  ListToolbar,
  PageHeader,
  Text,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { ExportButton } from '../../reports/components/ExportButton';
import { formatEuro } from '../../sales/format';
import { useRowMenu, useLoadedForAction } from '../../../app/rowActions';
import { SaleFormDialog } from '../../sales/components/SaleFormDialog';
import { TaskFormDialog } from '../../tasks/components/TaskFormDialog';
import { useCustomer, useCustomerAction, useCustomers, type CustomerListItem } from '../api';
import { CustomerFormDialog } from '../components/CustomerFormDialog';
import styles from './CrmPages.module.css';

const SCOPES = ['active', 'all'] as const;
const FILTER_KEYS = ['q', 'umfang'] as const;

/** Customers with search and sorting (CUS-002, CUS-003). */
export function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { can } = useSession();
  const [q, setQ] = useUrlState('q', '');
  const [scope, setScope] = useUrlState<(typeof SCOPES)[number]>('umfang', 'active', SCOPES);
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const columnSort = useColumnSort();
  const [creating, setCreating] = useState(false);
  const [rowAction, setRowAction] = useState<{
    kind: 'sale' | 'task' | 'edit';
    id: string;
  } | null>(null);
  const rowMenu = useRowMenu();
  const toast = useToast();
  const action = useCustomerAction();
  // Archiving is reversible, so it happens at once with an undo (§6.10).
  const setArchived = (id: string, value: boolean) =>
    action.mutate(
      { id, action: value ? 'archive' : 'unarchive' },
      {
        onSuccess: () =>
          toast.show(
            value ? t('crm.detail.archived') : t('crm.detail.unarchived'),
            'success',
            value
              ? {
                  label: t('common.undo'),
                  onClick: () => action.mutate({ id, action: 'unarchive' }),
                }
              : undefined,
          ),
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );
  const search = useDeferredValue(q.trim());
  const customers = useCustomers({ q: search, include_archived: scope === 'all' });
  const rows = customers.data ?? [];
  const filtered = search !== '' || scope !== 'active';
  const empty = !customers.isPending && rows.length === 0 && !filtered;
  const create = () => setCreating(true);

  const nameOrder = new Map(rows.map((c, i) => [c.id, String(i).padStart(6, '0')]));
  const columns: DataTableColumn<CustomerListItem>[] = [
    {
      key: 'name',
      header: t('crm.list.name'),
      primary: true,
      cell: (c) => (
        <>
          {c.name}
          {c.archived && (
            <>
              {' '}
              <Badge size="sm">{t('crm.archived')}</Badge>
            </>
          )}
        </>
      ),
      // The server sends the list in name order, persons by last name (CUS-002).
      sortValue: (c) => nameOrder.get(c.id),
    },
    {
      key: 'city',
      header: t('crm.list.city'),
      mobile: 'subtitle',
      cell: (c) => c.city ?? '–',
      sortValue: (c) => c.city,
    },
    {
      key: 'contact',
      header: t('crm.list.contact'),
      mobile: 'hidden',
      cell: (c) =>
        c.email || c.phone ? <CellText main={c.email ?? c.phone} sub={c.email && c.phone} /> : '–',
      sortValue: (c) => c.email || c.phone,
    },
    {
      key: 'last',
      header: t('crm.list.lastPurchase'),
      mobile: 'subtitle',
      cell: (c) => (c.stats?.last_purchase ? formatDate(c.stats.last_purchase) : '–'),
      sortValue: (c) => c.stats?.last_purchase,
    },
    {
      key: 'revenue',
      header: t('crm.list.revenue'),
      align: 'end',
      cell: (c) =>
        c.stats ? (
          <CellText
            main={formatEuro(c.stats.revenue_total_cents)}
            sub={
              c.stats.open_orders + c.stats.open_invoices > 0 &&
              t('crm.list.open', { count: c.stats.open_orders + c.stats.open_invoices })
            }
          />
        ) : (
          '–'
        ),
      sortValue: (c) => c.stats?.revenue_total_cents,
    },
  ];

  // One sort for all screens (#188): the column sort, on phones as a menu.
  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('crm.title')}
        actions={
          <>
            {can('customers.write') && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={create}>
                {t('crm.new')}
              </Button>
            )}
            <ExportButton list="customers" />
          </>
        }
      />

      {!empty && (
        <ListToolbar
          search={{
            value: q,
            onChange: setQ,
            label: t('common.search'),
            clearLabel: t('common.clear'),
          }}
          scope={{
            label: t('crm.scope.label'),
            value: scope,
            onChange: (v) => setScope(v as (typeof SCOPES)[number]),
            options: [
              { value: 'active', label: t('crm.scope.active') },
              { value: 'all', label: t('crm.scope.all') },
            ],
          }}
          sort={sortMenu}
        />
      )}

      <Card padding="none">
        {customers.error ? (
          <Text tone="danger" role="alert" className={styles.error}>
            {errorMessage(customers.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('crm.list.caption')}
            columns={columns}
            rows={rows}
            rowKey={(c) => c.id}
            rowHref={(c) => `/kunden/${c.id}`}
            rowActions={rowMenu.table<CustomerListItem>(
              (c) => c.name,
              (c) => [
                !c.archived &&
                  can('sales.write') && {
                    key: 'sale',
                    label: t('sales.quick.sale'),
                    onSelect: () => setRowAction({ kind: 'sale', id: c.id }),
                  },
                can('tasks.write') && {
                  key: 'task',
                  label: t('tasks.new'),
                  onSelect: () => setRowAction({ kind: 'task', id: c.id }),
                },
                // Archived customers stay editable, anonymised ones not (CUS-007).
                !c.anonymized &&
                  can('customers.write') &&
                  rowMenu.edit(() => setRowAction({ kind: 'edit', id: c.id })),
                can('customers.write') && {
                  key: 'archive',
                  label: c.archived ? t('crm.detail.unarchive') : t('crm.detail.archive'),
                  separated: true,
                  onSelect: () => setArchived(c.id, !c.archived),
                },
              ],
            )}
            {...columnSort}
            loading={customers.isPending}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon="users"
                  title={t('crm.list.emptyTitle')}
                  actions={
                    can('customers.write') && <Button onClick={create}>{t('crm.new')}</Button>
                  }
                />
              )
            }
          />
        )}
      </Card>

      {creating && (
        <CustomerFormDialog
          open
          onClose={() => setCreating(false)}
          onSaved={(c) => void navigate(`/kunden/${c.id}`)}
        />
      )}
      {rowAction?.kind === 'sale' && (
        <SaleFormDialog
          open
          customerId={rowAction.id}
          onClose={() => setRowAction(null)}
          onSaved={(s) => void navigate(`/verkauf/${s.id}`)}
        />
      )}
      {rowAction?.kind === 'task' && (
        <TaskFormDialog
          open
          context={{ customerId: rowAction.id }}
          onClose={() => setRowAction(null)}
        />
      )}
      {rowAction?.kind === 'edit' && (
        <EditCustomer id={rowAction.id} onClose={() => setRowAction(null)} />
      )}
    </div>
  );
}

/** Loads the full customer for the edit dialog opened from a row menu. */
function EditCustomer({ id, onClose }: { id: string; onClose: () => void }) {
  const customer = useLoadedForAction(useCustomer(id), onClose);
  return customer ? <CustomerFormDialog open customer={customer} onClose={onClose} /> : null;
}
